import 'devextreme/dist/css/dx.common.css';
import '../App.css';
import '../theme/innovyze-theme-devextreme.css';

import {
	getSubscriptions,
	isAdmin,
	useIsFeatureEnabled,
	warning,
	Page,
	STYLOVYZE_FLAGS,
	useSelectUserContext,
	InnovyzeRole,
} from '@innovyze/stylovyze';
import {
	fullAccess,
	inspectionAccess,
	mixedAccess,
	viewerAccess,
} from '@innovyze/shared-utils';
import {
	useActivitiesMainDefaultRoute,
	useActivitiesMainRoute,
	inspectionsRoutes,
	useMapDefaultRoutes,
	useRehabRoutes,
	useRiskRoutes,
	useLauncherRoutes,
	useVenusRoutes,
} from './pages';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '../i18n/useGlobalization';
// eslint-disable-next-line import/no-unresolved
import { useRoutes as useAdminPages } from 'fed_admin/Pages';
// eslint-disable-next-line import/no-unresolved
import { usePages as useAssetUploadPages } from 'fed_app_am_data_management/Pages';
import { isUserFullAccess } from '@Utils/isUserFullAccess';

// Static warned flag out here to prevent multiple warnings being displayed
let subscriptionWarned = false;

export default function usePages(): Page[] {
	let hasDefault = false;

	const dispatch = useDispatch();
	const { t } = useGlobalization();

	const pages: Page[] = [];

	// IMPORTANT - Please don't call hooks in an if statement, not even useIsFeatureEnabled, always assign to a variable first

	// IMPORTANT - these checked NEED to be done at this level otherwise we get a hook issue
	// So if you add a feature flag put it here then use it later
	const enableMap = useIsFeatureEnabled('info-360-am-map');
	const enableInspections = useIsFeatureEnabled('info-360-am-inspections');
	const enableRisk = useIsFeatureEnabled('info-360-am-risk');
	const enableRiskMultipleModels = useIsFeatureEnabled(
		'info-360-asset-multiple-risk-models',
	);
	const enableData = useIsFeatureEnabled('info-360-am-gis-import');
	const enableRehab = useIsFeatureEnabled('info-360-asset-rehab');
	const enableAdmin = useIsFeatureEnabled('info-360-am-admin');
	const isUserReadOnlyAdmin = useIsFeatureEnabled(
		STYLOVYZE_FLAGS.USER_READONLY_ADMIN,
	);

	const useLanucher = !!useIsFeatureEnabled('info-360-am-main-launcher');
	const enableVenus = !!useIsFeatureEnabled('info-360-am-venus');

	const { entitlements, innovyzeRole } = useSelectUserContext();

	const mapDefaultRoutes = useMapDefaultRoutes(t);
	const activitiesMainDefaultRoute = useActivitiesMainDefaultRoute(t);
	const activitiesMainRoute = useActivitiesMainRoute(t);
	const riskRoutes = useRiskRoutes(t, useLanucher);
	const rehabRoutes = useRehabRoutes(t, useLanucher);
	const launcherRoutes = useLauncherRoutes(t);
	const venusRoutes = useVenusRoutes();

	const adminPagesRoutes = useAdminPages();
	const assetUploadPages = useAssetUploadPages();

	const isAmAdminOrFullAccess =
		isAdmin() ||
		innovyzeRole == InnovyzeRole.READ_WRITE ||
		isUserFullAccess(entitlements);
	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);
	const hasInspectionAccess = inspectionAccess(subscriptions);
	const hasViewerAccess = viewerAccess(subscriptions);
	const isUserAdminAllowed = isUserReadOnlyAdmin && isAmAdminOrFullAccess;

	if (!subscriptionWarned && !hasFullAccess && mixedAccess(subscriptions)) {
		subscriptionWarned = true;
		dispatch(
			warning(
				'You have multiple Info360 subscriptions, you have been assigned viewer access by default.',
			),
		);
	}

	//TODO : When adding a new module add the checks for useIsFeatureEnabled (LaunchDarkly).

	if (enableMap && (hasFullAccess || hasViewerAccess)) {
		pages.push(...mapDefaultRoutes);
		hasDefault = true;
	}

	if (
		enableInspections &&
		(hasFullAccess || hasInspectionAccess || hasViewerAccess)
	) {
		pages.push(...(!hasDefault ? activitiesMainDefaultRoute : activitiesMainRoute));

		pages.push(...inspectionsRoutes);
		hasDefault = true;
	}

	if (useLanucher && (hasFullAccess || hasViewerAccess)) {
		pages.push(...launcherRoutes);

		if (enableVenus)
			pages.push(...venusRoutes);
	}

	if (enableRisk && (hasFullAccess || hasViewerAccess)) {
		pages.push(...riskRoutes);
	}

	if (enableRehab && (hasFullAccess || hasViewerAccess)) {
		pages.push(...rehabRoutes);
	}

	if (enableData && hasFullAccess) {
		pages.push(...assetUploadPages);
	}

	if (enableAdmin && isUserAdminAllowed) {
		pages.push(...adminPagesRoutes);
	}

	return pages;
}
